/* Trailer renders: white background, one ground line, one scale.

   WHY THIS EXISTS. The extension takes its render from Airtable, and an
   Airtable attachment URL is signed with an expiry stamped into it. Fine for a
   page rendered seconds after the search, useless for anything stored, which
   is why the history drawer kept coming back with dead images. These live in
   Shopify Files, so the URL is stable.

   THE FILES CARRY THE GEOMETRY, NOT THE STYLESHEET. Each file is cut to one
   scale and carries a fixed skirt of shadow below the tyre contact point, so
   bottom aligning two of them puts their wheels on the same line and width
   alone says how long a trailer is.

   WHICH SCALE. These are perspective renders, so the reference is the average
   of the near plane (jockey wheel, coupler) and the far plane (rear road
   wheel), roughly mid body. Normalising on either end alone makes the other
   end's wheels visibly different sizes. px per foot lands between 56.5 and
   58.0 across the four Sport models; the second batch (Tilt, RT500, Single,
   Tandem, Supercar, Enclosed, SHIFTR) was cut at 57.2 px/ft, checked against
   the Single: 17'9 5/8" is 1018 px.

   len is the file's own width as a fraction of the Pro Sport's 1378 px. Three
   models borrow a picture and keep their OWN length: Tandem Pro and Supercar
   Pro (24'4 3/8", pictures about 3ft short) and Motorcycle (14'6 1/4", borrows
   the Single, 14.52 ft x 57.21 = 831 px, 0.603). Enclosed is a ghost render and
   genuinely longer than the Pro Sport, so its len is over 1.

   w and h are each file's true pixel size, measured off the CDN on 21 Oct 2026.
   They are written onto every img this file creates or re-points, so the
   browser knows the aspect before the bytes arrive. Lighthouse was failing
   "image elements do not have explicit width and height" on every page.
   A file swapped for a new cut MUST have its numbers updated here, and since
   a filename is never reused on Shopify Files the version suffix is the cue.

   THE EXTENSION'S TRANSFORMS GET ZEROED. It scales the preview render by 1.2
   and card renders by 1.14 to fill wells that the old half empty files could
   not. fx-scale.css undoes it, but only on wells marked here.

   THE "WHICH TRAILER" DIALOG. Its two pictures come through here too, keyed on
   the name printed under each one. fx-drift.css hides the extension's picture
   until the well is marked; a model with no white render is marked
   data-fx-miss instead so its picture still shows.

   ---------------------------------------------------------------------------
   SWITCHING MARQUE USED TO FLICKER, FOR FOUR REASONS. All four are here.

   THE NAME COMES FROM THE ROW, NOT FROM localStorage. trailerData is only
   written when a search runs, so picking SHIFTR left it saying Super Sport and
   the sweep wrote the Super Sport render back over the SHIFTR one. The
   selector's own model chip changes on the pick, which is what
   fx-ext-brand.js reads too.

   THE KEY TOLERATES A LONGER NAME. "SHIFTR 1883" against a key of "SHIFTR":
   lookup falls back to the longest key contained in the name. Longest first,
   because "SUPERCAR PRO" contains "SUPERCAR".

   THE OLD RENDER HOLDS UNTIL THE NEW ONE IS READY. The new file is decoded
   off-DOM first, and while it is in flight any src this file did not write is
   put back, so the extension's Airtable picture never gets a frame.

   A REBUILT WELL GETS ITS PICTURE BEFORE THE PAINT. "Back to my VIN" throws
   the img away and builds a new one. The sweep runs inside the observer
   callback, a microtask, so it lands before paint; a render already decoded
   goes straight back in, and it fades only when the trailer actually changed.
*/
(function () {
  'use strict';

  var CDN = 'https://cdn.shopify.com/s/files/1/0963/8327/1200/files/';
  var FADE_MS = 220;

  var MAP = {
    'CLUB SPORT': { f: 'trailer-club-sport-v3.webp', len: 0.753, w: 1037, h: 245 },
    'SUPER SPORT': { f: 'trailer-super-sport-v3.webp', len: 0.885, w: 1219, h: 252 },
    'PRO SPORT': { f: 'trailer-pro-sport-v3.webp', len: 1, w: 1378, h: 255 },
    'SUPER TOURER': { f: 'trailer-super-tourer-v3.webp', len: 0.995, w: 1371, h: 583 },
    'TILT': { f: 'trailer-tilt-v1.webp', len: 0.747, w: 1030, h: 283 },
    'RT500': { f: 'trailer-rt500-v1.webp', len: 0.872, w: 1201, h: 197 },
    'SINGLE': { f: 'trailer-single-v1.webp', len: 0.739, w: 1018, h: 321 },
    'MOTORCYCLE': { f: 'trailer-single-v1.webp', len: 0.603, w: 1018, h: 321 },
    'TANDEM': { f: 'trailer-tandem-v1.webp', len: 0.875, w: 1206, h: 325 },
    'TANDEM PRO': { f: 'trailer-tandem-v1.webp', len: 1.006, w: 1206, h: 325 },
    'SUPERCAR': { f: 'trailer-supercar-v1.webp', len: 0.875, w: 1206, h: 423 },
    'SUPERCAR PRO': { f: 'trailer-supercar-v1.webp', len: 1.006, w: 1206, h: 423 },
    'ENCLOSED': { f: 'trailer-enclosed-v1.webp', len: 1.012, w: 1394, h: 617 },
    'SHIFTR': { f: 'trailer-shiftr-1883-v1.webp', len: 0.934, w: 1287, h: 472 }
  };

  /* BOAT TRAILERS. Not a market the parts store serves, so there is no
     Airtable render and the VIN hero used to fall back to text only, which
     read as broken. These are the NZ site's studio renders, lifted to white and
     cut like the rest. Picked from the model code: S or T is the axle count,
     the first digit is the boat length in metres (S-475-B is a single axle for
     a 4.75m boat). len is approximate and only has to look plausible. */
  var BOAT = {
    S4: { f: 'trailer-boat-s4-v1.webp', len: 0.71, w: 1094, h: 245 },
    S5: { f: 'trailer-boat-s5-v1.webp', len: 0.82, w: 1086, h: 247 },
    S6: { f: 'trailer-boat-s6-v1.webp', len: 0.95, w: 994, h: 264 },
    T5: { f: 'trailer-boat-t5-v1.webp', len: 0.9, w: 1305, h: 299 },
    T6: { f: 'trailer-boat-t6-v1.webp', len: 1, w: 1313, h: 310 },
    T7: { f: 'trailer-boat-t7-v1.webp', len: 1.08, w: 1534, h: 363 }
  };
  var BOAT_CODE = /(?:^|[^A-Z0-9])([ST])\s*-?\s*(\d)(?:\d|[A-D])/;

  function boatEntry(raw) {
    var s = String(raw == null ? '' : raw).toUpperCase();
    var m = BOAT_CODE.exec(s);
    if (!m && s.indexOf('BOAT') < 0) return null;
    if (!m) return BOAT.S5;
    var metres = +m[2];
    if (m[1] === 'T') return metres <= 5 ? BOAT.T5 : metres === 6 ? BOAT.T6 : BOAT.T7;
    return metres <= 4 ? BOAT.S4 : metres === 5 ? BOAT.S5 : BOAT.S6;
  }

  /* Longest first, so a name that contains two keys matches the specific one. */
  var KEYS = Object.keys(MAP).sort(function (a, b) { return b.length - a.length; });

  /* Every render this page has fully decoded. A URL in here can be put on
     screen in the same task that noticed the wrong one, with nothing to wait
     for. */
  var READY = {};

  function norm(s) {
    return String(s == null ? '' : s).toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();
  }

  function entry(name) {
    var n = norm(name);
    if (!n) return null;
    if (MAP[n]) return MAP[n];
    for (var i = 0; i < KEYS.length; i++) {
      if (n.indexOf(KEYS[i]) > -1) return MAP[KEYS[i]];
    }
    return boatEntry(name);
  }

  function urlFor(name) {
    var e = entry(name);
    return e ? CDN + e.f : '';
  }
  function lenFor(name) {
    var e = entry(name);
    return e ? e.len : 0;
  }
  window.fxRenderFor = urlFor;
  window.fxLenFor = lenFor;

  /* Written on every img this file creates or re-points. Compared before
     writing, same rule as mark(): the sweep runs on every mutation and an
     attribute write invalidates style even when the value is identical. */
  function sizeImg(img, name) {
    var e = entry(name);
    if (!e || !e.w) return;
    if (img.getAttribute('width') !== String(e.w)) img.setAttribute('width', e.w);
    if (img.getAttribute('height') !== String(e.h)) img.setAttribute('height', e.h);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function onScreenName() {
    try {
      var d = JSON.parse(window.localStorage.getItem('trailerData') || 'null');
      return d ? (d.displayModelName || d.modelName || '') : '';
    } catch (e) { return ''; }
  }

  /* What the selector row is showing right now. This changes on the pick,
     before any search is run, which is the whole point of preferring it. */
  function rowName() {
    var row = document.querySelector('.vin-modal .vin-fx-context-row');
    if (!row) return '';
    var chip = row.querySelector('[data-chip-wrap="model"] .vin-fx-selchip-val');
    if (chip && chip.textContent.trim()) return chip.textContent;
    var lab = row.querySelector('.vin-fx-mobile-filter-label');
    if (lab && lab.textContent.trim()) return lab.textContent;
    return '';
  }

  /* A boat trailer found by VIN. The selector matrix has no boats, so the
     chips fall back to a Sport model; the hero's own title and pills are what
     actually describe the trailer. Returns the text to key the render on, or
     '' when this is not a boat. */
  function heroBoat() {
    var t = document.querySelector('.vin-modal #vinTrailerTitle');
    if (!t || !/boat/i.test(t.textContent || '')) return '';
    var bits = ['#vinFxHeroPills', '#vinTrailerExtraSpecs', '#vinTrailerDescription'];
    var s = t.textContent;
    for (var i = 0; i < bits.length; i++) {
      var el = document.querySelector('.vin-modal ' + bits[i]);
      if (el) s += ' ' + el.textContent;
    }
    return s;
  }

  /* The extension switches the hero to text only when it has no picture. For
     a boat we do have one, so switch it back. Checked every pass, because the
     extension re-applies the class whenever it re-renders the results. */
  function showBoatHero() {
    if (!heroBoat()) return;
    var sec = document.querySelector('.vin-modal .vin-results-trailer--text-only');
    if (sec) sec.classList.remove('vin-results-trailer--text-only');
    var col = document.querySelector('.vin-modal #vinTrailerRenderColumn');
    if (col && col.hasAttribute('aria-hidden')) col.removeAttribute('aria-hidden');
    var img = document.querySelector('.vin-modal #vinTrailerImage');
    if (img && img.classList.contains('is-placeholder')) img.classList.remove('is-placeholder');
  }

  /* Each place a render appears knows the model by a different route. The
     picked trailer reads its own printed name rather than trailerData, because
     browsing the filters leaves trailerData empty while the preview is still
     showing a model quite happily. */
  function nameNear(img) {
    var card = img.closest('.ident-card');
    if (card) {
      var n = card.querySelector('.ident-card-name');
      return n ? n.textContent : '';
    }
    var sheet = img.closest('.ident-sheet');
    if (sheet) {
      var h = sheet.querySelector('.fx-ms__name, #vinIdentSheetTitle, h3');
      return h ? h.textContent : '';
    }
    var pv = img.closest('.ident-preview');
    if (pv) {
      var p = pv.querySelector('.ident-preview-name');
      return p && p.textContent.trim() ? p.textContent : onScreenName();
    }
    var dr = img.closest('.vin-fx-drift-opt');
    if (dr) {
      var dn = dr.querySelector('.vin-fx-drift-name');
      return dn ? dn.textContent : '';
    }
    if (img.classList.contains('vin-fx-trailer-img')) {
      return heroBoat() || rowName() || onScreenName();
    }
    /* Anything else sitting in an extension image slot (the quiz's result
       cards, for one) carries its model key on the slot itself. */
    var slot = img.closest('[data-ident-media]');
    if (slot) return slot.getAttribute('data-ident-media') || '';
    return '';
  }

  function isDrift(well) {
    return !!(well && well.classList && well.classList.contains('vin-fx-drift-shot'));
  }

  function mark(well, name) {
    if (!well) return;
    /* WRITTEN ONLY WHEN IT CHANGES. The sweep runs on every mutation and on a
       slow interval underneath it, and an inline style write invalidates an
       element's style even when the value is identical. */
    if (well.hasAttribute('data-fx-miss')) well.removeAttribute('data-fx-miss');
    if (well.getAttribute('data-fx-white') !== '1') well.setAttribute('data-fx-white', '1');
    var want = String(lenFor(name) || 1);
    if (well.style.getPropertyValue('--fx-len') !== want) well.style.setProperty('--fx-len', want);
  }

  function miss(well) {
    if (isDrift(well) && !well.hasAttribute('data-fx-miss')) well.setAttribute('data-fx-miss', '1');
  }

  /* The fade only plays when the trailer actually changes. When the extension
     rebuilds the well and hands us a brand new img for the SAME trailer (which
     is what "Back to my VIN" does), the right picture goes straight back with
     no fade, because fading a picture that was already there reads as a blink. */
  function put(img, u, well, name) {
    var changed = !well || well.fxShown !== u;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    sizeImg(img, name);
    img.setAttribute('src', u);
    img.fxWant = u;
    img.fxHave = u;
    if (well) well.fxShown = u;
    /* The scale changes with the picture, not before it, or the outgoing
       trailer is drawn at the incoming one's length for a frame. */
    mark(well, name);
    if (changed && typeof img.animate === 'function' && !reducedMotion()) {
      img.animate([{ opacity: 0 }, { opacity: 1 }],
        { duration: FADE_MS, easing: 'cubic-bezier(.25, .1, .25, 1)' });
    }
  }

  function apply(img) {
    var name = nameNear(img);
    var u = urlFor(name);
    var well = img.closest('.ident-media, .vin-fx-hero-image, .vin-fx-drift-shot') || img.parentElement;

    if (!u) {
      if (well) {
        if (well.hasAttribute('data-fx-white')) well.removeAttribute('data-fx-white');
        if (well.style.getPropertyValue('--fx-len')) well.style.removeProperty('--fx-len');
        well.fxShown = null;
      }
      miss(well);
      img.fxWant = null;
      img.fxHave = null;
      return;
    }

    /* Compared against the live src, not against a stamp, so that a re-render
       which puts the Airtable URL back is corrected rather than ignored. */
    if (img.getAttribute('src') === u) {
      img.fxWant = u;
      img.fxHave = u;
      if (well) well.fxShown = u;
      sizeImg(img, name);
      mark(well, name);
      return;
    }

    /* Already decoded once on this page: put it back now. This is the path a
       re-render takes, and because the sweep runs inside the mutation
       observer's callback, it lands before the browser paints the extension's
       picture. */
    if (READY[u]) {
      put(img, u, well, name);
      return;
    }

    if (img.fxWant === u) {
      /* Ours is still decoding. If anything else has changed src in the
         meantime, put back the last render this file chose, so the extension's
         own asset never gets a frame on screen. Writing src here fires the
         observer again, but the next pass finds src already equal to fxHave
         and does nothing, so this settles rather than loops. */
      if (img.fxHave && img.getAttribute('src') !== img.fxHave) {
        img.setAttribute('src', img.fxHave);
      }
      return;
    }

    img.fxWant = u;

    var pre = new Image();
    pre.onload = function () {
      READY[u] = true;
      if (img.fxWant !== u) return;             // superseded by a later pick
      if (img.getAttribute('src') === u) return;
      put(img, u, well, name);
    };
    pre.onerror = function () {
      /* Leave whatever the extension had rather than swap in a broken file,
         and clear the claim so a later sweep can try again. In the dialog the
         extension's picture is hidden until marked, so show it. */
      if (img.fxWant === u) img.fxWant = null;
      miss(well);
    };
    pre.src = u;
  }

  /* EMPTY SLOTS GET OUR RENDER, AND NEVER THE WORDS. The extension paints its
     image slots from one network request for the Airtable render map. Reload
     and scroll quickly, or have that request fail, and every slot is painted
     "Render not uploaded yet", which reads as a broken store. Every model has a
     render in the map above, so a slot is filled from here the moment it
     appears, with no request to wait on, and the extension is told the slot
     is painted so it leaves it alone. A slot whose model is not in the map
     keeps the loading ring rather than the sentence. */
  function fillSlots() {
    var slots = document.querySelectorAll('[data-ident-media]');
    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];
      if (slot.querySelector('img')) continue;
      var key = slot.getAttribute('data-ident-media') || '';
      var u = urlFor(key);
      if (slot.classList.contains('ident-media--empty')) {
        slot.classList.remove('ident-media--empty');
        slot.textContent = '';
      }
      if (!slot.classList.contains('is-loading')) slot.classList.add('is-loading');
      if (!u) continue;
      if (slot.getAttribute('data-ident-media-painted') !== '1') slot.setAttribute('data-ident-media-painted', '1');
      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      sizeImg(img, key);
      (function (s) {
        var done = function () { s.classList.remove('is-loading'); };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      })(slot);
      img.setAttribute('src', u);
      img.fxWant = u;
      img.fxHave = u;
      slot.fxShown = u;
      slot.appendChild(img);
      mark(slot, key);
      var card = slot.closest('.ident-card');
      if (card) {
        card.classList.remove('ident-card--no-render');
        if (card.hidden) card.hidden = false;
      }
    }
  }

  function sweep() {
    showBoatHero();
    fillSlots();
    var imgs = document.querySelectorAll('.ident-media > img, img.vin-fx-trailer-img, .vin-fx-drift-shot > img');
    for (var i = 0; i < imgs.length; i++) apply(imgs[i]);
  }

  /* RUN IN THE OBSERVER'S CALLBACK, NOT 16ms LATER. Mutation observer callbacks
     are microtasks, which run before the browser paints. The old setTimeout
     gave the browser a frame to show whatever the extension had just written,
     and that frame was the blink on the way back from a filtered trailer to the
     VIN one. The sweep is a handful of images and each one returns at the
     first comparison when nothing is wrong, so running it per batch is cheap,
     and the src writes it makes settle on the next pass. */
  function start() {
    sweep();
    new MutationObserver(sweep).observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['src']
    });
    setInterval(sweep, 800);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
